import type { ScenarioParams } from "../lib/types";

interface Props {
  scenario: ScenarioParams;
  setScenario: (s: ScenarioParams) => void;
}

const PRESETS: { key: string; label: string; bruto: number }[] = [
  { key: "off", label: "Geen", bruto: 0 },
  { key: "current", label: "Huidig (8.20)", bruto: 8.20 },
  { key: "expanded", label: "Uitgebreid (10.64)", bruto: 10.64 },
];

export function ScenarioBar({ scenario, setScenario }: Props) {
  const accuKwh = scenario.accu_bruto_kwh * (scenario.accu_marge_pct / 100);
  const presetActive = (key: string) => {
    if (key === "custom") {
      return !PRESETS.some((p) => p.bruto === scenario.accu_bruto_kwh);
    }
    return PRESETS.find((p) => p.key === key)?.bruto === scenario.accu_bruto_kwh;
  };

  return (
    <div className="scenario-card">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h2 className="serif m-0 text-[22px] font-medium">Scenario</h2>
        <span className="help">Wijzig om de impact direct te zien.</span>
      </div>

      <div className="grid gap-4">
        {/* JAAR */}
        <div>
          <div className="label-up mb-1.5">Jaar / contractperiode</div>
          <div className="toggle-group flex-wrap">
            <button
              className={`toggle-btn ${scenario.year === 2026 ? "active" : ""}`}
              onClick={() => setScenario({ ...scenario, year: 2026 })}
            >
              2026 (mét saldering)
            </button>
            <button
              className={`toggle-btn ${scenario.year === "transition_2026_2027" ? "active" : ""}`}
              onClick={() => setScenario({ ...scenario, year: "transition_2026_2027" })}
            >
              Transitie jun '26 – mei '27
            </button>
            <button
              className={`toggle-btn ${scenario.year === 2027 ? "active" : ""}`}
              onClick={() => setScenario({ ...scenario, year: 2027 })}
            >
              2027 (zónder)
            </button>
          </div>
          {scenario.year === "transition_2026_2027" && (
            <div className="help mt-1.5 fade-in">
              Contract loopt 1 juni 2026 – 31 mei 2027. Saldering werkt jun‑dec
              2026 (zomer-piek productie), vervalt vanaf jan 2027 (apr/mei
              productie zonder saldering).
            </div>
          )}
        </div>

        {/* CURTAILMENT — info, geen toggle (per contract auto-optimaal) */}
        <div className="bg-bg p-3 rounded-md help">
          <strong className="text-ink">Curtailment</strong>: per contract automatisch
          toegepast op uren waarop netto‑opbrengst negatief is (vaste contracten: nooit;
          dynamische contracten: alleen onder break‑even). Per‑contract resultaat zie je op de cards.
        </div>

        {/* ACCU */}
        <div>
          <div className="label-up mb-1.5">
            Accu — bruikbaar nu:{" "}
            <span className="mono text-ink">{accuKwh.toFixed(2)} kWh</span>
          </div>
          <div className="toggle-group flex-wrap">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                className={`toggle-btn ${presetActive(p.key) ? "active" : ""}`}
                onClick={() => setScenario({ ...scenario, accu_bruto_kwh: p.bruto })}
              >
                {p.label}
              </button>
            ))}
            <button
              className={`toggle-btn ${presetActive("custom") ? "active" : ""}`}
              onClick={() =>
                setScenario({ ...scenario, accu_bruto_kwh: scenario.accu_bruto_kwh || 8 })
              }
            >
              Eigen waarde
            </button>
          </div>
          {presetActive("custom") && (
            <div className="grid grid-cols-2 gap-3 mt-3 fade-in">
              <div className="field">
                <span className="label-up">Bruto capaciteit (kWh)</span>
                <input
                  className="input"
                  type="number"
                  step="0.5"
                  value={scenario.accu_bruto_kwh}
                  onChange={(e) =>
                    setScenario({
                      ...scenario,
                      accu_bruto_kwh: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="field">
                <span className="label-up">Bruikbare marge (%)</span>
                <input
                  className="input"
                  type="number"
                  step="5"
                  min="50"
                  max="100"
                  value={scenario.accu_marge_pct}
                  onChange={(e) =>
                    setScenario({
                      ...scenario,
                      accu_marge_pct: parseFloat(e.target.value) || 80,
                    })
                  }
                />
              </div>
            </div>
          )}
        </div>

        {/* ACCU STRATEGIE */}
        {scenario.accu_bruto_kwh > 0 && (
          <div>
            <div className="label-up mb-1.5">Accu strategie</div>
            <div className="toggle-group">
              <button
                className={`toggle-btn ${scenario.accu_strategy === "conservative" ? "active" : ""}`}
                onClick={() =>
                  setScenario({ ...scenario, accu_strategy: "conservative" })
                }
              >
                Conservatief
              </button>
              <button
                className={`toggle-btn ${scenario.accu_strategy === "smart" ? "active" : ""}`}
                onClick={() =>
                  setScenario({
                    ...scenario,
                    accu_strategy: "smart",
                    accu_charge_kw: scenario.accu_bruto_kwh > 9 ? 4.8 : 2.4,
                  })
                }
              >
                Smart (laden in goedkope uren)
              </button>
            </div>
            {scenario.accu_strategy === "smart" && (
              <div className="mt-2 flex items-center gap-2 fade-in">
                <span className="label-up">Laadvermogen (kW)</span>
                <input
                  type="number"
                  step="0.1"
                  value={scenario.accu_charge_kw}
                  onChange={(e) =>
                    setScenario({
                      ...scenario,
                      accu_charge_kw: parseFloat(e.target.value) || 2.4,
                    })
                  }
                  className="input mono w-24"
                />
                <span className="help">
                  Zendure SF2400: 2.4 kW. Met 2e SF2400 parallel: 4.8 kW.
                </span>
              </div>
            )}
            <div className="help mt-1.5">
              {scenario.accu_strategy === "conservative"
                ? "Alleen surplus solar laadt accu. Ontlaadt bij elk verbruik. Geen prijs-driven gedrag."
                : "Laadt accu uit net tijdens goedkoopste uren (typisch nacht), spaart accu voor duurste uren (typisch avond). Voordelig bij dynamisch contract; bij vast contract neutraal/nadelig."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
